// src/components/wordbook/WordbookForm.tsx
import {FC, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import {ArrowBack, Save} from '@mui/icons-material';
import {
    useCreateWordbook,
    useUpdateWordbook,
    useWordbook
} from '../../hooks/useWordbook';
import {CreateWordbookRequest} from '../../models/wordbook/createWordbookRequest';
import {Visibility} from '../../types/wordbook';
import {ImageUpload} from '../common/ImageUpload';

export const WordbookForm: FC = () => {
    const { id, groupId: paramGroupId } = useParams<{ id: string; groupId?: string }>();
    const navigate = useNavigate();
    const isEditMode = !!id;
    const wordbookId = id ? parseInt(id) : 0;
    const initialGroupId = paramGroupId ? parseInt(paramGroupId) : undefined;

    const { data: wordbook, isLoading } = useWordbook(wordbookId);

    const createWordbook = useCreateWordbook();
    const updateWordbook = useUpdateWordbook();

    const [formData, setFormData] = useState<CreateWordbookRequest>({
        title: '',
        headLang: 'en',
        meaningLang: 'ko',
        visibility: Visibility.VISIBLE,
        groupId: initialGroupId,
    });

    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        if (wordbook) {
            setFormData({
                title: wordbook.title,
                headLang: wordbook.headLang,
                meaningLang: wordbook.meaningLang,
                visibility: wordbook.visibility,
                thumbnailUrl: wordbook.thumbnailUrl || undefined,
            });
        }
    }, [wordbook]);

    const handleChange = <K extends keyof CreateWordbookRequest>(field: K, value: CreateWordbookRequest[K]) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.title?.trim()) {
            newErrors.title = '제목을 입력해주세요.';
        }

        if (!isEditMode) {
            if (!formData.headLang) {
                newErrors.headLang = '학습 언어를 선택해주세요.';
            }
            if (!formData.meaningLang) {
                newErrors.meaningLang = '의미 언어를 선택해주세요.';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        if (isEditMode) {
            updateWordbook.mutate(
                { id: wordbookId, data: formData },
                {
                    onSuccess: () => {
                        navigate(`/wordbook/${wordbookId}`);
                    },
                }
            );
        } else {
            createWordbook.mutate(formData, {
                onSuccess: (response) => {
                    if (formData.groupId) {
                        navigate(`/wordbook-group/${formData.groupId}`);
                    } else {
                        navigate(`/wordbook/${response.wordbookId}`);
                    }
                },
            });
        }
    };

    const handleCancel = () => {
        if (formData.groupId) {
            navigate(`/wordbook-group/${formData.groupId}`);
        } else {
            navigate(isEditMode ? `/wordbook/${wordbookId}` : '/wordbook');
        }
    };

    if (isEditMode && isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (isEditMode && !isLoading && !wordbook) {
        return (
            <Box p={3}>
                <Alert severity="error">단어장을 찾을 수 없습니다.</Alert>
            </Box>
        );
    }

    const mutation = isEditMode ? updateWordbook : createWordbook;

    return (
        <Box p={3}>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={handleCancel}
                >
                    {isEditMode ? '뒤로가기' : '목록으로'}
                </Button>
                <Typography variant="h4">
                    {isEditMode ? '단어장 수정' : '새 단어장 만들기'}
                </Typography>
            </Box>

            {mutation.isError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {mutation.error?.message ||
                        `단어장 ${isEditMode ? '수정' : '생성'}에 실패했습니다.`}
                </Alert>
            )}

            <Grid container spacing={3}>
                {/* 기본 정보 */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                                기본 정보
                            </Typography>

                            {/* 제목 */}
                            <TextField
                                fullWidth
                                label="제목"
                                value={formData.title}
                                onChange={(e) => handleChange('title', e.target.value)}
                                error={!!errors.title}
                                helperText={errors.title}
                                required={!isEditMode}
                                margin="normal"
                            />

                            {/* 언어 설정 */}
                            <Grid container spacing={2} mt={1}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <FormControl
                                        fullWidth
                                        error={!!errors.headLang}
                                        required={!isEditMode}
                                    >
                                        <InputLabel>학습 언어</InputLabel>
                                        <Select
                                            value={formData.headLang || ''}
                                            onChange={(e) => handleChange('headLang', e.target.value)}
                                            label="학습 언어"
                                        >
                                            <MenuItem value="ko">한국어</MenuItem>
                                            <MenuItem value="en">영어</MenuItem>
                                            <MenuItem value="ja">일본어</MenuItem>
                                            <MenuItem value="zh">중국어</MenuItem>
                                            <MenuItem value="es">스페인어</MenuItem>
                                            <MenuItem value="fr">프랑스어</MenuItem>
                                            <MenuItem value="de">독일어</MenuItem>
                                            <MenuItem value="vi">베트남어</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <FormControl
                                        fullWidth
                                        error={!!errors.meaningLang}
                                        required={!isEditMode}
                                    >
                                        <InputLabel>의미 언어</InputLabel>
                                        <Select
                                            value={formData.meaningLang || ''}
                                            onChange={(e) => handleChange('meaningLang', e.target.value)}
                                            label="의미 언어"
                                        >
                                            <MenuItem value="ko">한국어</MenuItem>
                                            <MenuItem value="en">영어</MenuItem>
                                            <MenuItem value="ja">일본어</MenuItem>
                                            <MenuItem value="zh">중국어</MenuItem>
                                            <MenuItem value="es">스페인어</MenuItem>
                                            <MenuItem value="fr">프랑스어</MenuItem>
                                            <MenuItem value="de">독일어</MenuItem>
                                            <MenuItem value="vi">베트남어</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            {/* 노출 설정 */}
                            <FormControl fullWidth margin="normal">
                                <InputLabel>노출 상태</InputLabel>
                                <Select
                                    value={formData.visibility}
                                    onChange={(e) => handleChange('visibility', e.target.value as Visibility)}
                                    label="노출 상태"
                                >
                                    <MenuItem value={Visibility.VISIBLE}>노출</MenuItem>
                                    <MenuItem value={Visibility.HIDDEN}>숨김</MenuItem>
                                </Select>
                            </FormControl>

                            {/* Create 모드에서만 표시되는 필드들 */}
                            {!isEditMode && (
                                <>
                                    <TextField
                                        fullWidth
                                        label="소유자 ID (선택사항)"
                                        type="number"
                                        value={formData.ownerId || ''}
                                        onChange={(e) => handleChange('ownerId', e.target.value ? parseInt(e.target.value) : undefined)}
                                        helperText="비워두면 시스템 단어장으로 생성됩니다."
                                        margin="normal"
                                    />

                                    <TextField
                                        fullWidth
                                        label="그룹 ID (선택사항)"
                                        type="number"
                                        value={formData.groupId || ''}
                                        onChange={(e) => handleChange('groupId', e.target.value ? parseInt(e.target.value) : undefined)}
                                        helperText="특정 그룹에 포함시키려면 그룹 ID를 입력하세요."
                                        margin="normal"
                                    />
                                </>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* 썸네일 업로드 */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card>
                        <CardContent>
                            <ImageUpload
                                value={formData.thumbnailUrl}
                                onChange={(url) => handleChange('thumbnailUrl', url || undefined)}
                                folder="wordbook/thumbnails"
                                label="썸네일 이미지"
                                helperText="권장 크기: 800x600px, 최대 5MB"
                            />
                        </CardContent>
                    </Card>
                </Grid>

                {/* 버튼 */}
                <Grid size={{ xs: 12 }}>
                    <Box display="flex" justifyContent="flex-end" gap={2}>
                        <Button
                            variant="outlined"
                            onClick={handleCancel}
                            disabled={mutation.isPending}
                        >
                            취소
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<Save />}
                            onClick={handleSubmit}
                            disabled={mutation.isPending}
                        >
                            {mutation.isPending
                                ? `${isEditMode ? '저장' : '생성'} 중...`
                                : isEditMode ? '저장' : '생성'}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};
